import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Button, Spin, Tabs } from 'antd';
import { ReloadOutlined, CloudOutlined, MailOutlined, RadarChartOutlined, RocketOutlined } from '@ant-design/icons';
import { useDevDashboard } from '../../hooks/useDevDashboard';
import { devApi } from '../../services/devApi';
import type { ChainListItem, ChainDetail, ChainStats, ChainFilters } from '../../services/devApi';
import StatsCards from './components/StatsCards';
import ModuleTable from './components/ModuleTable';
import ModuleDetailDrawer from './components/ModuleDetailDrawer';
import TestSummaryPanel from './components/TestSummaryPanel';
import TestTrendChart from './components/TestTrendChart';
import BugReportPanel from './components/BugReportPanel';
import ChainStatsCards from './components/ChainStatsCards';
import ChainTable from './components/ChainTable';
import ChainDetailDrawer from './components/ChainDetailDrawer';
import GCSBucketPanel from './components/GCSBucketPanel';
import InboxMonitorPanel from './components/InboxMonitorPanel';
import FlowObservePanel from './components/FlowObservePanel';
import ChainCockpitPanel from './components/ChainCockpitPanel';

const { Title, Text } = Typography;

function formatLastUpdated(date: Date | null): string {
  if (!date) return '--';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
}

const DEFAULT_CHAIN_FILTERS: ChainFilters = {
  search: '',
  domain: '',
  status: '',
  evidence: '',
  formId: '',
  page: 1,
  size: 20,
};

const DevDashboard: React.FC = () => {
  const {
    stats,
    modules,
    modulesTotal,
    moduleDetail,
    drawerVisible,
    testRuns,
    testRunsTotal,
    testTrend,
    lastUpdated,
    loading,
    filters,
    testRunFilters,
    trendFilters,
    refreshAll,
    handleStatusFilter,
    handleSearch,
    handlePageChange,
    handleSort,
    handleModuleClick,
    handleDrawerClose,
    handleTestRunFilterChange,
    handleTrendFilterChange,
  } = useDevDashboard();

  // ============================================================
  // Chain state management
  // ============================================================
  const [chainStats, setChainStats] = useState<ChainStats | null>(null);
  const [chains, setChains] = useState<ChainListItem[]>([]);
  const [chainsTotal, setChainsTotal] = useState(0);
  const [chainDetail, setChainDetail] = useState<ChainDetail | null>(null);
  const [chainDrawerVisible, setChainDrawerVisible] = useState(false);
  const [chainFilters, setChainFilters] = useState<ChainFilters>(DEFAULT_CHAIN_FILTERS);
  const [chainLoading, setChainLoading] = useState({
    stats: false,
    list: false,
    detail: false,
  });
  const [activeTab, setActiveTab] = useState('modules');

  const fetchChainStats = useCallback(async () => {
    setChainLoading((prev) => ({ ...prev, stats: true }));
    try {
      const data = await devApi.getChainStats();
      setChainStats(data);
    } catch (err) {
      console.error('Fetch chain stats error:', err);
    } finally {
      setChainLoading((prev) => ({ ...prev, stats: false }));
    }
  }, []);

  const fetchChains = useCallback(async (f: ChainFilters) => {
    setChainLoading((prev) => ({ ...prev, list: true }));
    try {
      const res = await devApi.getChains({
        page: f.page,
        size: f.size,
        search: f.search || undefined,
        domain: f.domain || undefined,
        status: f.status || undefined,
        evidence: f.evidence || undefined,
        formId: f.formId || undefined,
      });
      setChains(res.data);
      setChainsTotal(res.total);
    } catch (err) {
      console.error('Fetch chains error:', err);
    } finally {
      setChainLoading((prev) => ({ ...prev, list: false }));
    }
  }, []);

  const fetchChainDetail = useCallback(async (chainId: string) => {
    setChainLoading((prev) => ({ ...prev, detail: true }));
    try {
      const data = await devApi.getChain(chainId);
      setChainDetail(data);
    } catch (err) {
      console.error('Fetch chain detail error:', err);
    } finally {
      setChainLoading((prev) => ({ ...prev, detail: false }));
    }
  }, []);

  // Load chain data when tab switches to chains
  useEffect(() => {
    if (activeTab === 'chains') {
      fetchChainStats();
      fetchChains(chainFilters);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch chains when filters change
  useEffect(() => {
    if (activeTab === 'chains') {
      fetchChains(chainFilters);
    }
  }, [chainFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChainSearch = useCallback((value: string) => {
    setChainFilters((prev) => ({ ...prev, search: value, page: 1 }));
  }, []);

  const handleChainDomainFilter = useCallback((value: string) => {
    setChainFilters((prev) => ({ ...prev, domain: value, page: 1 }));
  }, []);

  const handleChainStatusFilter = useCallback((value: string) => {
    setChainFilters((prev) => ({ ...prev, status: value, page: 1 }));
  }, []);

  const handleChainPageChange = useCallback((page: number, size: number) => {
    setChainFilters((prev) => ({ ...prev, page, size }));
  }, []);

  const handleChainRowClick = useCallback((chainId: string) => {
    setChainDrawerVisible(true);
    fetchChainDetail(chainId);
  }, [fetchChainDetail]);

  const handleChainDrawerClose = useCallback(() => {
    setChainDrawerVisible(false);
    setChainDetail(null);
  }, []);

  const isAnyLoading = Object.values(loading).some(Boolean);

  const modulesTab = (
    <>
      {/* Section A: Stats Cards */}
      <div className="mb-6">
        <StatsCards
          stats={stats}
          loading={loading.stats}
          onStatusClick={handleStatusFilter}
        />
      </div>

      {/* Section B: Module Table */}
      <div className="mb-6">
        <Spin spinning={loading.modules}>
          <ModuleTable
            modules={modules}
            total={modulesTotal}
            loading={loading.modules}
            filters={filters}
            selectedFormId={moduleDetail?.formId || null}
            onSearch={handleSearch}
            onStatusFilter={handleStatusFilter}
            onPageChange={handlePageChange}
            onSort={handleSort}
            onRowClick={handleModuleClick}
          />
        </Spin>
      </div>

      {/* Section C2: Bug Reports */}
      <div className="mb-6">
        <BugReportPanel />
      </div>

      {/* Section D: Test Summary */}
      <div className="mb-6">
        <TestSummaryPanel
          stats={stats}
          testRuns={testRuns}
          testRunsTotal={testRunsTotal}
          loading={loading.testRuns}
          filters={testRunFilters}
          onFilterChange={handleTestRunFilterChange}
        />
      </div>

      {/* Section E: Trend Chart */}
      <div className="mb-6">
        <TestTrendChart
          trendData={testTrend}
          loading={loading.testTrend}
          filters={trendFilters}
          modules={modules}
          onFilterChange={handleTrendFilterChange}
        />
      </div>

      {/* Section C: Detail Drawer (overlay) */}
      <ModuleDetailDrawer
        visible={drawerVisible}
        module={moduleDetail}
        loading={loading.moduleDetail}
        onClose={handleDrawerClose}
      />
    </>
  );

  const chainsTab = (
    <>
      {/* Chain Stats */}
      <div className="mb-6">
        <ChainStatsCards stats={chainStats} loading={chainLoading.stats} />
      </div>

      {/* Chain Table */}
      <div className="mb-6">
        <ChainTable
          chains={chains}
          total={chainsTotal}
          loading={chainLoading.list}
          filters={chainFilters}
          onSearch={handleChainSearch}
          onDomainFilter={handleChainDomainFilter}
          onStatusFilter={handleChainStatusFilter}
          onPageChange={handleChainPageChange}
          onRowClick={handleChainRowClick}
        />
      </div>

      {/* Chain Detail Drawer */}
      <ChainDetailDrawer
        visible={chainDrawerVisible}
        chain={chainDetail}
        loading={chainLoading.detail}
        onClose={handleChainDrawerClose}
      />
    </>
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>
            ERP 模組開發進度儀表板
          </Title>
          <Text type="secondary">375 個模組 | VFP → Go + React</Text>
        </div>
        <div className="flex items-center gap-4">
          <Text type="secondary">
            最後更新：{formatLastUpdated(lastUpdated)}
          </Text>
          <Button
            icon={<ReloadOutlined spin={isAnyLoading} />}
            onClick={refreshAll}
            loading={isAnyLoading}
          >
            刷新
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        defaultActiveKey="modules"
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'modules', label: '模組進度', children: modulesTab },
          { key: 'chains', label: '業務鏈條', children: chainsTab },
          { key: 'gcs', label: <span className="flex items-center gap-1.5"><CloudOutlined />GCS Bucket</span>, children: <GCSBucketPanel /> },
          { key: 'inbox', label: <span className="flex items-center gap-1.5"><MailOutlined />收件夾監控</span>, children: <InboxMonitorPanel /> },
          { key: 'flow-observe', label: <span className="flex items-center gap-1.5"><RadarChartOutlined />資料流觀測</span>, children: <FlowObservePanel /> },
          { key: 'chain-cockpit', label: <span className="flex items-center gap-1.5"><RocketOutlined />鏈駕駛艙</span>, children: <ChainCockpitPanel onGoFlowObserve={() => setActiveTab('flow-observe')} /> },
        ]}
      />
    </div>
  );
};

export default DevDashboard;
